/**
 * Tipo di configurazione CACER (D.M. 414/2023 Art. 3 c. 2).
 * - cer: Comunità Energetica Rinnovabile (soggetto giuridico autonomo)
 * - autoconsumatori_collettivi: Autoconsumatori collettivi (condomini/edifici)
 * - autoconsumatori_individuali_distanza: Autoconsumo individuale a distanza
 */
export type TipoConfigurazione =
	| 'cer'
	| 'autoconsumatori_collettivi'
	| 'autoconsumatori_individuali_distanza';
/** Zona geografica per il bonus tariffa GSE (Appendice B § 2 D.M. 414/2023) */
export type ZonaGeografica = 'nord' | 'centro' | 'sud';

export interface CERConfig {
	id: string;
	tenantId: string;
	nome: string;
	codiceGSE?: string;
	email?: string;
	comune?: string;
	/** Computed from linked PODs, plants and non-withdrawn members; never persisted. */
	soci: number;
	podProd: number;
	podCons: number;
	sociRes: number;
	kwp: number;
	/** Annual typical production / installed power; null if profiles are incomplete. */
	ore: number | null;
	/** Annual typical withdrawal in MWh; null if profiles are incomplete. */
	cons: number | null;
	battery: number;
	productionMwh?: number | null;
	missingProfiles?: number;
	missingStorage?: number;
	consResPct: number | null;
	// ── Parametri GSE in energia ──────────────────────────────────────────────
	/** Zona geografica — determina il bonus tariffa (Appendice B § 2 D.M. 414/2023) */
	zonaGeografica?: ZonaGeografica;
	/**
	 * % capacità da impianti pre-esistenti (<16/12/2021) sul totale kWp.
	 * Max 30% — regola 70/30 (ALLEGATO 1 § 3.3).
	 */
	preEsistentePct?: number;
	/**
	 * Contributo in conto capitale ricevuto (% — es. 40 per 40%).
	 * Incide sul fattore F (Appendice B § 3).
	 * 0 = nessun contributo.
	 */
	pnrrGrantPct?: number;
	/**
	 * Area convenzionale / identificativo cabina primaria GSE.
	 *
	 * ARCHITETTURA MULTI-CONFIGURAZIONE (D.M. 414/2023 Art. 3 c. 2 lett. d):
	 * Una CER (soggetto giuridico) può gestire più "configurazioni" (CACER), una per ogni
	 * area geografica sottesa a una cabina primaria diversa.
	 *
	 * Regola: tutti i POD di una configurazione devono ricadere nell'area sottesa alla
	 * MEDESIMA cabina primaria. Se i soci abitano in zone con cabine primarie diverse,
	 * occorre creare tante configurazioni quante le cabine primarie.
	 *
	 * Gli incentivi (acconto mensile e conguaglio annuale) vengono calcolati e liquidati
	 * PER CONFIGURAZIONE, non per CER nel suo complesso. Il GSE produce un contratto separato
	 * e rendicontazioni separate per ciascuna configurazione.
	 *
	 * In SLIMS CER, ogni record CERConfig rappresenta UNA configurazione.
	 * Per CER multi-configurazione, creare più CERConfig con lo stesso codiceGSE
	 * ma diverso areaConvenzionale e numeroConfigurazione.
	 */
	areaConvenzionale?: string;
	/**
	 * Tipo di configurazione CACER (D.M. 414/2023 Art. 3 c. 2).
	 * Determina il regime normativo e l'algoritmo di calcolo degli incentivi.
	 */
	tipoConfigurazione?: TipoConfigurazione;
	/**
	 * Codice identificativo cabina primaria GSE/Terna.
	 * Tutti i POD della configurazione devono ricadere sotto la stessa cabina primaria.
	 */
	cabinaPrimariaCodice?: string;
	/**
	 * Data attivazione Contratto di Servizio GSE (SPC) — avvia il periodo incentivante di 20 anni.
	 * Scadenza domanda: 120 giorni dall'entrata in esercizio dell'impianto.
	 */
	dataAttivazioneGSE?: string;
	settings?: string;
	/**
	 * Conteggio delle anagrafiche registrate (inclusi i receduti); soci conta solo i non receduti.
	 * Iniettato dall'API /api/cer — non persiste in DB.
	 */
	membersCount?: number;
}

export const DEFAULT_CER_CONFIG: Omit<CERConfig, 'id' | 'tenantId'> = {
	nome: '',
	soci: 0,
	podProd: 0,
	podCons: 0,
	sociRes: 0,
	kwp: 0,
	ore: 0,
	cons: 0,
	battery: 0,
	consResPct: 0,
};
